import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { loadChatMessagesHandler } from '../../../application/chat-messages/handlers/LoadChatMessagesHandler';
import { useProviderServices } from '../../../app/providers/AppServicesContext';
import { ChatProviderType } from '../../../domain/chat/models/ChatProviderType';
import { resolveSessionAgent } from '../../../domain/resolvers/resolveSessionAgent';
import { selectSelectedMessages } from '../../../domain/selectors/selected/chat/selectSelectedMessages';
import { selectSelectedSession } from '../../../domain/selectors/selected/chat/selectSelectedSession';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { prependMessages, setMessages } from '../../../redux/features/chatMessages/chatMessagesSlice';
import { setHasMoreMessages, setIsLoadingMessages, setLoadedMessageCount } from '../../../redux/features/chatUi/chatUiSlice';
import {
	clearMigrationContext,
	MIGRATION_BANNER_TTL_MS,
} from '../../../redux/features/migrationContext/migrationContextSlice';
import { acquireApiAccessTokenAsync, acquireNexusApiAccessTokenAsync } from '../../../services/auth/AuthService';
import { alerts } from '../../../shared/ui/Alerts';
import { CHAT_MESSAGES_PAGE_SIZE, supportsMessagePagination } from '../../../application/chat-messages/chatMessagesPaging';

export interface ConversationStarterOption {
	title: string;
	text: string;
}

export interface MigrationInfo {
	migratedFromTitle: string;
	migrationSummary: string;
}

export interface UseChatRoomVm {
	hasSession: boolean;
	sessionId: string | null;
	messagesLoaded: boolean;
	isLoadingMessages: boolean;
	messagesEmpty: boolean;
	messages: ReturnType<typeof selectSelectedMessages>;
	agentImageName?: string;
	agentProviderType?: ChatProviderType;
	conversationStarterOptions: ConversationStarterOption[];
	migrationInfo?: MigrationInfo;
	prefillText?: string;
	prefillVersion: number;
	hasMoreMessages: boolean;
	onStarterClick: (starter: ConversationStarterOption) => void;
	onDismissMigrationBanner: () => void;
	loadMoreMessages: () => void;
}


export const useChatRoom = (): UseChatRoomVm => {
	const dispatch = useAppDispatch();
	const providerServices = useProviderServices();
	const { instance } = useMsal();
	const session = useAppSelector(selectSelectedSession);
	const messages = useAppSelector(selectSelectedMessages);
	const messagesLoaded = useAppSelector((state) =>
		session ? state.chatMessages.bySessionId[session.id] !== undefined : false,
	);
	const isLoadingMessages = useAppSelector((state) =>
		session ? Boolean(state.chatUi.bySessionId[session.id]?.isLoadingMessages) : false,
	);
	const hasMoreMessages = useAppSelector((state) =>
		session ? Boolean(state.chatUi.bySessionId[session.id]?.hasMoreMessages) : false,
	);
	const loadedMessageCount = useAppSelector((state) =>
		session ? (state.chatUi.bySessionId[session.id]?.loadedMessageCount ?? 0) : 0,
	);
	const isLoadingMoreRef = useRef(false);
	const agentsById = useAppSelector((state) => state.agents.byId);
	const resolvedAgent = useMemo(
		() => (session ? resolveSessionAgent(session, agentsById) : null),
		[agentsById, session],
	);
	const [prefillText, setPrefillText] = useState<string | undefined>();
	const [prefillVersion, setPrefillVersion] = useState(0);

	const conversationStarterOptions = useMemo<ConversationStarterOption[]>(() => {
		if (!resolvedAgent) {
			return [];
		}

		const seen = new Set<string>();
		const starters: ConversationStarterOption[] = [];

		for (const group of resolvedAgent.conversationStarters) {
			for (const starter of group.items) {
				if (starter.isLargeBlock) {
					continue;
				}

				const title = (starter.title?.trim() || starter.prompt.trim());
				if (!title || seen.has(title)) {
					continue;
				}

				seen.add(title);
				starters.push({
					title,
					text: starter.prompt?.trim() || title,
				});

				if (starters.length >= 10) {
					return starters;
				}
			}
		}

		return starters;
	}, [resolvedAgent]);

	const onStarterClick = useCallback((starter: ConversationStarterOption): void => {
		setPrefillText(starter.text);
		setPrefillVersion((prev) => prev + 1);
	}, []);

	const onDismissMigrationBanner = useCallback((): void => {
		if (session) {
			dispatch(clearMigrationContext(session.id));
		}
	}, [dispatch, session]);

	useEffect(() => {
		setPrefillText(undefined);
		setPrefillVersion(0);
	}, [session?.id]);

	useEffect(() => {
		const loadAsync = async (): Promise<void> => {
			if (!session || messagesLoaded || isLoadingMessages) {
				return;
			}

			dispatch(
				setIsLoadingMessages({
					sessionId: session.id,
					value: true,
				}),
			);

			try {
				const accessToken = session.providerType === ChatProviderType.Nexus
					? await acquireNexusApiAccessTokenAsync({ msalInstance: instance })
					: await acquireApiAccessTokenAsync({ msalInstance: instance });

				const loadedMessages = await loadChatMessagesHandler({
					session,
					accessToken,
					providerServices,
					skip: 0,
					count: CHAT_MESSAGES_PAGE_SIZE,
				});

				dispatch(setMessages({ sessionId: session.id, messages: loadedMessages }));
				dispatch(setHasMoreMessages({ sessionId: session.id, value: supportsMessagePagination(session.providerType) && loadedMessages.length === CHAT_MESSAGES_PAGE_SIZE }));
				dispatch(setLoadedMessageCount({ sessionId: session.id, value: loadedMessages.length }));
			} catch {
				// Mark messages as loaded with an empty array so the guard above prevents retrying on every render.
				// Without this, a failed load keeps messagesLoaded = false and triggers an infinite loop.
				dispatch(setMessages({ sessionId: session.id, messages: [] }));
				alerts.error('Failed to load messages. Please refresh or try again.');
			} finally {
				dispatch(
					setIsLoadingMessages({
						sessionId: session.id,
						value: false,
					}),
				);
			}
		};

		void loadAsync();
	}, [dispatch, instance, isLoadingMessages, messagesLoaded, providerServices, session]);

	const loadMoreMessages = useCallback((): void => {
		if (!session || !hasMoreMessages || isLoadingMoreRef.current) return;
		if (!supportsMessagePagination(session.providerType)) return;

		isLoadingMoreRef.current = true;

		const fetchAsync = async (): Promise<void> => {
			try {
				const accessToken = session.providerType === ChatProviderType.Nexus
					? await acquireNexusApiAccessTokenAsync({ msalInstance: instance })
					: await acquireApiAccessTokenAsync({ msalInstance: instance });
				const olderMessages = await loadChatMessagesHandler({
					session,
					accessToken,
					providerServices,
					skip: loadedMessageCount,
					count: CHAT_MESSAGES_PAGE_SIZE,
				});

				dispatch(prependMessages({ sessionId: session.id, messages: olderMessages }));
				dispatch(setHasMoreMessages({ sessionId: session.id, value: olderMessages.length === CHAT_MESSAGES_PAGE_SIZE }));
				dispatch(setLoadedMessageCount({ sessionId: session.id, value: loadedMessageCount + olderMessages.length }));
			} catch {
				alerts.error('Failed to load older messages.');
			} finally {
				isLoadingMoreRef.current = false;
			}
		};

		void fetchAsync();
	}, [dispatch, hasMoreMessages, instance, loadedMessageCount, providerServices, session]);

	const migrationContextEntry = useAppSelector((state) =>
		session ? state.migrationContext.bySessionId[session.id] : undefined,
	);
	const migrationInfo = useMemo(() => {
		if (!migrationContextEntry) return undefined;
		if (Date.now() - migrationContextEntry.createdAt >= MIGRATION_BANNER_TTL_MS) return undefined;
		return migrationContextEntry;
	}, [migrationContextEntry]);

	return {
		hasSession: session !== null,
		sessionId: session?.id ?? null,
		messagesLoaded,
		isLoadingMessages,
		messagesEmpty: messages.length === 0,
		messages,
		agentImageName: resolvedAgent?.agentIconUrl,
		agentProviderType: session?.providerType,
		conversationStarterOptions,
		migrationInfo,
		prefillText,
		prefillVersion,
		hasMoreMessages,
		onStarterClick,
		onDismissMigrationBanner,
		loadMoreMessages,
	};
};

export default useChatRoom;
